import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LOGO_URL } from "@/lib/brand";
import { ArrowLeft, ArrowRight } from "lucide-react";

export const ONBOARDED_KEY = "km_onboarded";

const STEPS = [
  {
    art: "/art/onboard-jar.png",
    color: "#29C7E8",
    title: "YOUR MONEY, ALL IN ONE PLACE",
    lines: [
      "This is a little home for the money you earn, save and spend.",
      "The big number at the top is always what you have right now.",
    ],
  },
  {
    art: "/art/onboard-buttons.png",
    color: "#7CE645",
    title: "TAP MAKE OR SPEND",
    lines: [
      "Earned some money? Tap MAKE and punch in the amount.",
      "Bought something? Tap SPEND and say what it was.",
    ],
  },
  {
    art: "/art/onboard-goal.png",
    color: "#FF4D9D",
    title: "SAVE UP FOR SOMETHING",
    lines: [
      "Add something you really want and its price.",
      "It lands on your timeline, and the bar fills up as you save.",
    ],
  },
];

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isFirstRun = params.get("first") === "1";

  useEffect(() => {
    sessionStorage.removeItem("km_first_run");
  }, []);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const finish = () => {
    localStorage.setItem(ONBOARDED_KEY, "1");
    navigate("/", { replace: true });
  };

  return (
    <div
      data-testid="onboarding-page"
      className="min-h-screen flex flex-col items-center px-4 py-8"
    >
      <img src={LOGO_URL} alt="KIDS MONEY" className="h-20 sm:h-24 w-auto object-contain mb-5" />

      <div
        data-testid={`onboarding-step-${step}`}
        className="chunky-card w-full max-w-md p-6 text-center"
        style={{ backgroundColor: "#FFFDF6" }}
      >
        <div
          className="rounded-3xl border-4 border-navy mb-5 flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: current.color, height: 190 }}
        >
          <img
            src={current.art}
            alt=""
            className="max-h-[150px] w-auto object-contain goal-wobble"
          />
        </div>

        <h1 className="font-display text-3xl sm:text-4xl text-navy leading-tight">
          {current.title}
        </h1>
        <div className="mt-3 space-y-2">
          {current.lines.map((line) => (
            <p key={line} className="font-display text-base sm:text-lg text-navy/75">
              {line}
            </p>
          ))}
        </div>

        {isLast && (
          <p
            data-testid="onboarding-parent-note"
            className="font-display text-sm text-navy/70 mt-5 rounded-2xl border-4 border-navy px-4 py-3 text-left"
            style={{ backgroundColor: "#FFF3C2" }}
          >
            <span className="uppercase">For parents:</span> tap the gear to add a profile for
            each kid. Every kid gets their own money, goals and history — and we only ever ask
            for a first name. No kid emails, no bank or card linking.
          </p>
        )}

        <div className="flex items-center justify-center gap-2 mt-6">
          {STEPS.map((s, i) => (
            <span
              key={s.title}
              className="rounded-full border-2 border-navy transition-all"
              style={{
                width: i === step ? 26 : 12,
                height: 12,
                backgroundColor: i === step ? "#FF5A36" : "white",
              }}
            />
          ))}
        </div>

        <div className="flex gap-3 mt-5">
          {step > 0 && (
            <button
              data-testid="onboarding-back-btn"
              onClick={() => setStep((s) => s - 1)}
              className="chunky-btn px-5 py-3 text-lg text-navy bg-white flex items-center gap-1"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={3} />
              BACK
            </button>
          )}
          <button
            data-testid={isLast ? "onboarding-done-btn" : "onboarding-next-btn"}
            onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
            className="chunky-btn flex-1 py-3 text-xl text-navy flex items-center justify-center gap-2"
            style={{ backgroundColor: isLast ? "#7CE645" : "#FFC400" }}
          >
            {isLast ? "LET'S GO!" : "NEXT"}
            {!isLast && <ArrowRight className="w-5 h-5" strokeWidth={3} />}
          </button>
        </div>
      </div>

      {!isFirstRun && (
        <button
          data-testid="onboarding-close-btn"
          onClick={() => navigate("/", { replace: true })}
          className="mt-6 font-display text-base text-navy/60 underline"
        >
          Back to the app
        </button>
      )}
      {isFirstRun && !isLast && (
        <button
          data-testid="onboarding-skip-btn"
          onClick={finish}
          className="mt-6 font-display text-base text-navy/60 underline"
        >
          Skip for now
        </button>
      )}
    </div>
  );
}
